import { cn } from "@plasma/utils/cn";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

export interface ComboboxItem<T extends string> {
  value: T;
  label: string;
  keywords?: string[];
  imageUrl?: string;
  component?: React.ReactNode;
}

export const Combobox = <T extends string>({
  items,
  value,
  onValueChange,
  disabled,
  dropdownClassName,
  className,
  placeholder,
  createNewOption,
  filter,
}: {
  items: ComboboxItem<T>[];
  value: T | null;
  onValueChange: (value: T | null) => void;
  disabled?: boolean;
  dropdownClassName?: string;
  className?: string;
  placeholder?: string;
  createNewOption?: {
    label: string;
    onClick: (name: string) => void;
  };
  filter?: (value: string, search: string, keywords?: string[]) => number;
}) => {
  const [open, setOpen] = useState(false);

  const currentItem = items.find((item) => item.value === value);
  const [query, setQuery] = useState("");

  return (
    <Popover
      onOpenChange={(v) => {
        setOpen(disabled ? false : v);
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button
          aria-disabled={disabled}
          aria-expanded={open}
          className={cn(
            "body aria-expanded:ring-ring grid w-full min-w-full grid-cols-[1fr_1.25rem] overflow-hidden font-sans focus-visible:ring-1 aria-expanded:ring-1",
            "group transition-none active:scale-100 disabled:border-solid aria-disabled:border",
            className
          )}
          disabled={disabled}
          id="combobox"
          role="combobox"
          size="lg"
          type="button"
          variant="outline"
        >
          <div
            className={cn(
              "text-muted-foreground group-disabled:text-muted-foreground w-full truncate text-left",
              currentItem && "text-foreground"
            )}
          >
            {currentItem ? (
              <div className="flex items-center gap-2">
                {currentItem.imageUrl && (
                  <img
                    alt={currentItem.label}
                    className="size-5 rounded-full"
                    height={20}
                    src={currentItem.imageUrl}
                    width={20}
                  />
                )}
                {currentItem.label}
              </div>
            ) : (
              "Please select"
            )}
          </div>
          <ChevronDown className="ml-2 size-5 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={twMerge("w-52 p-0", dropdownClassName)}
      >
        <Command
          filter={
            createNewOption
              ? (optionValue, search, keywords) => {
                  if (optionValue === "create-new") {
                    return 1;
                  }
                  if (filter) {
                    return filter(optionValue, search, keywords);
                  }
                  return !!keywords?.some((k) => k.includes(search)) ||
                    optionValue.includes(search)
                    ? 1
                    : 0;
                }
              : filter
          }
        >
          <CommandInput
            onValueChange={setQuery}
            placeholder={placeholder ?? "Search..."}
            value={query}
          />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  className="justify-between"
                  key={item.value}
                  keywords={item.keywords}
                  onSelect={(rawCurrentValue) => {
                    if (rawCurrentValue === value) {
                      onValueChange(null);
                    } else {
                      const selected = items.find(
                        (option) => option.value === rawCurrentValue
                      );
                      if (selected) {
                        onValueChange(selected.value);
                      }
                    }
                    setOpen(false);
                  }}
                  value={item.value}
                >
                  {item.component ?? (
                    <>
                      <div className="flex items-center gap-2">
                        {item.imageUrl && (
                          <img
                            alt={item.label}
                            className="size-5 rounded-full"
                            height={20}
                            src={item.imageUrl}
                            width={20}
                          />
                        )}
                        {item.label}
                      </div>
                      <Check
                        className={twJoin(
                          "mr-2 size-4",
                          value === item.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </>
                  )}
                </CommandItem>
              ))}
              {createNewOption && (
                <CommandItem
                  className="text-foreground items-center gap-2"
                  keywords={[createNewOption.label]}
                  onSelect={() => createNewOption.onClick(query)}
                  value="create-new"
                >
                  <Plus className="size-5" />
                  {createNewOption.label}
                  {query && `: "${query}"`}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
